/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */
import * as React from 'react';
import { useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route as UnsecureRoute, Switch, useRouteMatch } from 'react-router-dom';
import { getConfig } from '../../../store/config/actions';
import { syncWhatsNewPollingSession } from '../../../store/whatsNew/actions';
import AircraftFleetMapContainer from '../../fleet/AircraftFleetMapContainer';
import MapContainer from '../../fleet/MapContainer';
import FleetViewHeaderContents from '../../fleet/FleetViewHeaderContents';
import { FullTailViewContainerWithFeatureFlag } from '../../fleet/FullTailView/FullTailViewContainer';
import FullTailViewHeaderContents from '../../fleet/FullTailView/FullTailViewHeaderContents';
import FullTailViewMapContainer from '../../fleet/FullTailView/FullTailViewMapContainer';
import FullTailViewPrintViewHeaderContent from '../../fleet/FullTailViewExport/FullTailViewPrintViewHeaderContent';
import HubsContainer from '../../fleet/Hubs/HubsContainer';
import WeatherOverlay from '../../fleet/WeatherOverlay';
import MaintenanceContainer from '../../maintenance/MaintenanceContainer';
import MaintenanceViewHeaderContents from '../../maintenance/MaintenanceViewHeaderContents';
import FlightInfoContainer from '../../plans/FlightInformation/FlightInfoContainer';
import MissionDetailsContainer from '../../plans/MissionDetails/MissionDetailsContainer';
import PlansContainer from '../../plans/PlansContainer';
import PerformanceOutlookContainer from '../../plans/PerformanceOutlook/PerformanceOutlookContainer';
import PerformanceOutlookMap from '../../plans/PerformanceOutlook/PerformanceOutlookMap';
import PerformanceOutlookExportContainer from '../../plans/PerformanceOutlook/Export/PerformanceOutlookExportContainer';
import TicketsContainer from '../../tickets/TicketsContainer';
import TicketsViewHeaderContents from '../../tickets/TicketsViewHeaderContents';
import { MapContext } from '../map/BaseMapComponent';
import OverlaysPanel from '../map_overlays/OverlaysPanel';
import PerformanceOutlookLegendPanel from '../map_overlays/PerformanceOutlookLegendPanel';
import TailTagOverlaysPanel from '../map_overlays/TailTagOverlaysPanel';
import MapSettingsPanel from '../map_settings/MapSettingsPanel';
import appTheme from '../theme/appTheme.json';
import AppWrapper from './AppWrapper';
import Body from './Body';
import BodyWrapper from './BodyWrapper';
import ErrorBoundary from './ErrorBoundary';
import Header from './header/Header';
import HeaderActions from './header/HeaderActions';
import { MenuItems } from './menu/MenuItems';
import ModalLoadingSpinner from './ModalLoadingSpinner';
import ScrollbarsPrintModeWrapper from './ScrollbarsPrintModeWrapper';
import ViewHeader from './view_header/ViewHeader';
import DataUsageContainer from '../../dataUsage/DataUsageContainer';
import ProvisioningContainer from '../../provisioning/ProvisioningContainer';
import ManualDowntimeContainer from '../../provisioning/manualDowntime/ManualDowntimeContainer';
import AuthContainer from '../../auth/AuthContainer';
import { SecureRoute } from '@okta/okta-react';
import { LOGIN_CALLBACK_ROUTE, LOGIN_ROUTE } from '../../../utils/AuthUtils';
import { selectIsAuthenticated, selectIsInitialConfigRequestFinished, selectUserData } from '../../../store';
import { isMockMode } from '../../../utils/GlobalUtils';
import MockSecureRoute from '../MockSecureRoute';
import TransportMonitoringContainer from '../../isanPortal/transportMonitoring/TransportMonitoringContainer';
import FullSpaceVehicleViewContainer from '../../isanPortal/transportMonitoring/FullSpaceVehicleView/FullSpaceVehicleViewContainer';
import FullSpaceVehicleViewHeaderContainer from '../../isanPortal/transportMonitoring/FullSpaceVehicleView/FullSpaceVehicleViewHeaderContainer';
import FullSpaceVehicleViewMapContainer from '../../isanPortal/transportMonitoring/FullSpaceVehicleView/FullSpaceVehicleViewMapContainer';
import RulerToolDistanceSummary from '../map_overlays/RulerToolDistanceSummary';
import { KeyboardArrowDown } from '@mui/icons-material';
import type { } from '@mui/x-date-pickers/themeAugmentation';
import CustomLoginCallback from '../../auth/CustomLoginCallback';
import { areAllowedRolesPresent } from '../../../utils/RBACUtils';
import { Authorities } from '../../../shared/entities/user';

const theme = createTheme({
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: appTheme.primaryBackgroundColor,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          height: '35px',
          fontSize: '13px',
          border: '1px solid',
          borderRadius: '26px',
          fontWeight: 600,
          fontFamily: appTheme.appFontFamily,
          textAlign: 'center',
          textTransform: 'none',
          ':hover': {
            cursor: 'pointer',
            backgroundColor: 'inherit',
            svg: {
              color: '#ffffff',
            },
          },
        },
        textPrimary: {
          color: appTheme.primaryButtonTextColor,
        },
        textSecondary: {
          color: appTheme.secondaryButtonTextColor,
        },
      },
      variants: [{ props: { variant: 'text' }, style: { border: 'none' } }],
      defaultProps: {},
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          color: '#FFFFFF80',
          '&.Mui-active': {
            color: appTheme.buttonSelectedColor,
          },
          '&:hover': { color: appTheme.buttonSelectedColor, opacity: 0.5 },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          backgroundColor: appTheme.primaryBackgroundColor,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          height: '20px',
          lineHeight: '0px',
          color: appTheme.primaryTextColor,
          fontSize: appTheme.defaultFontSize,
          fontFamily: appTheme.viasatFontFamilyRegular,
          borderBottomColor: appTheme.mptRowBorderBottom,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: appTheme.primaryTextColor,
          fontFamily: appTheme.appFontFamily,
          backgroundColor: appTheme.primaryBackgroundColor,
          ':hover': {
            backgroundColor: appTheme.secondaryBackgroundColor,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          color: appTheme.primaryTextColor,
          backgroundColor: appTheme.primaryBackgroundColor,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: appTheme.primaryButtonTextColor,
          size: 'large',
        },
        colorPrimary: { color: appTheme.primaryButtonTextColor },
        colorSecondary: { color: appTheme.buttonIconColor },
        disabled: { color: appTheme.disabledIconColor },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: appTheme.primaryButtonTextColor,
          '&.Mui-checked': {
            color: appTheme.primaryButtonTextColor,
          },
        },
        colorPrimary: { color: appTheme.primaryButtonTextColor },
        colorSecondary: { color: appTheme.buttonIconColor },
        disabled: { color: appTheme.disabledIconColor },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: appTheme.primaryButtonTextColor,
          '&.Mui-checked': {
            color: appTheme.primaryButtonTextColor,
            colorSecondary: { color: 'blue !important' },
          },
        },
        colorPrimary: { color: appTheme.primaryButtonTextColor },
        colorSecondary: { color: appTheme.buttonIconColor },
        disabled: { color: appTheme.disabledIconColor },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: appTheme.mptFormLabelText,
          fontFamily: appTheme.appFontFamily,
          '&.Mui-focused': {
            color: appTheme.mptFormLabelText,
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: appTheme.appFontFamily,
          color: appTheme.primaryTextColor,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '24px',
          borderTop: `1px solid #32424E`,
        },
        spacing: {
          gap: '4px',
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: KeyboardArrowDown,
      },
    },
    MuiAccordionSummary: {
      defaultProps: {
        expandIcon: <KeyboardArrowDown sx={{ fontSize: '24px !important', color: appTheme.primaryTextColor }} />,
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        popupIcon: <KeyboardArrowDown />,
      },
    },
    MuiDesktopDatePicker: {
      defaultProps: {
        slots: {
          switchViewIcon: KeyboardArrowDown,
        },
      },
    },
    MuiDesktopDateTimePicker: {
      defaultProps: {
        slots: {
          switchViewIcon: KeyboardArrowDown,
        },
      },
    },
  },
  palette: {
    primary: {
      main: appTheme.primaryButtonColor,
    },
    secondary: {
      main: appTheme.secondaryButtonColor,
    },
    error: {
      light: appTheme.errorTextColor,
      main: appTheme.errorTextColor,
      dark: appTheme.errorTextColor,
    },
    contrastThreshold: 3,
  },
});

const FleetMap = React.lazy(() => import('../../fleet/FleetMap'));

function App() {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isInitialConfigRequestFinished = useSelector(selectIsInitialConfigRequestFinished);
  const userData = useSelector(selectUserData);
  const hasWhatsNewPanelRole = areAllowedRolesPresent(userData, Authorities.ROLE_WHATS_NEW_PANEL);
  const userEmail = userData?.claims?.email || userData?.claims?.preferred_username;

  const dispatch = useDispatch();

  const isPlansPage = useRouteMatch(`/${MenuItems.plans}`);

  const isDataUsagePage = useRouteMatch(`/${MenuItems.dataUsage}`);

  const isProvisioningPage = useRouteMatch(`/${MenuItems.provisioning}`);

  const isManualDowntimePage = useRouteMatch(`/${MenuItems.manualDowntime}`);

  const hideViewHeader =
    !isAuthenticated || isPlansPage || isDataUsagePage || isProvisioningPage || isManualDowntimePage;

  const disableDrawer = !isAuthenticated;

  // Retrieve config when logged in
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getConfig());
    }
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    // Start polling only after app config is loaded so polling interval comes from backend config.
    if (isAuthenticated && !isInitialConfigRequestFinished) {
      return;
    }

    syncWhatsNewPollingSession(isAuthenticated, hasWhatsNewPanelRole, userEmail)(dispatch);
  }, [dispatch, hasWhatsNewPanelRole, isAuthenticated, isInitialConfigRequestFinished, userEmail]);

  // When running in mock mode, use a MockSecureRoute so that the Okta context is not required.
  const Route: React.FC<any> = isMockMode() ? MockSecureRoute : SecureRoute;

  //  TODO could probably do more with this "Loading..." will do for now...
  return (
    <AppWrapper className="app-wrapper">
      <ThemeProvider theme={theme}>
        <Header actions={<HeaderActions />} disableDrawer={disableDrawer} />
        <ViewHeader hidden={hideViewHeader}>
          <Switch>
            <Route exact key={`${MenuItems.fleet}`} path={`/${MenuItems.fleet}`} component={FleetViewHeaderContents} />
            <Route
              exact
              key={`${MenuItems.fleet}/datapathUuid`}
              path={`/${MenuItems.fleet}/:datapathUuid`}
              component={FullTailViewHeaderContents}
            />
            <Route
              exact
              key={`${MenuItems.fleet}/datapathUuid/export`}
              path={`/${MenuItems.fleet}/:datapathUuid/export`}
              component={FullTailViewPrintViewHeaderContent}
            />
            <Route
              exact
              key={`${MenuItems.maintenance}`}
              path={`/${MenuItems.maintenance}`}
              component={MaintenanceViewHeaderContents}
            />
            <Route
              exact
              key={`${MenuItems.tickets}`}
              path={`/${MenuItems.tickets}`}
              component={TicketsViewHeaderContents}
            />
            <Route
              exact
              key={`/${MenuItems.transportMonitoring}/vehicleId`}
              path={`/${MenuItems.transportMonitoring}/:vehicleId`}
              component={FullSpaceVehicleViewHeaderContainer}
            />
          </Switch>
        </ViewHeader>
        <Body hiddenViewHeader={hideViewHeader} hiddenDrawer={disableDrawer}>
          <ScrollbarsPrintModeWrapper>
            <BodyWrapper>
              <React.Suspense fallback={<ModalLoadingSpinner />}>
                <Switch>
                  <Redirect exact key="root" from="/" to={MenuItems.fleet} />

                  <UnsecureRoute exact key="login" path={LOGIN_ROUTE} component={AuthContainer} />
                  <UnsecureRoute
                    exact
                    key="login-reset-password"
                    path={`${LOGIN_ROUTE}/reset`}
                    component={AuthContainer}
                  />
                  <UnsecureRoute
                    exact
                    key="login-callback"
                    path={LOGIN_CALLBACK_ROUTE}
                    component={CustomLoginCallback}
                  />

                  <Route
                    exact
                    key={MenuItems.fleet}
                    path={`/${MenuItems.fleet}`}
                    render={() => (
                      <ErrorBoundary message="Unable to render fleet components">
                        <FleetMap />
                      </ErrorBoundary>
                    )}
                  />
                  <Route
                    exact
                    key={`/${MenuItems.fleet}/datapathUuid`}
                    path={`/${MenuItems.fleet}/:datapathUuid`}
                    render={() => (
                      <ErrorBoundary message="Unable to render the full terminal details components">
                        <FullTailViewContainerWithFeatureFlag />
                      </ErrorBoundary>
                    )}
                  />
                  <Route
                    exact
                    key={`/${MenuItems.fleet}/datapathUuid/export`}
                    path={`/${MenuItems.fleet}/:datapathUuid/export`}
                    render={() => (
                      <ErrorBoundary message="Unable to render the full terminal details export components">
                        <FullTailViewContainerWithFeatureFlag />
                      </ErrorBoundary>
                    )}
                  />
                  <Route
                    exact
                    key={MenuItems.maintenance}
                    path={`/${MenuItems.maintenance}`}
                    render={() => <MaintenanceContainer />}
                  />
                  <Route
                    exact
                    key={MenuItems.tickets}
                    path={`/${MenuItems.tickets}`}
                    render={() => (
                      <ErrorBoundary message="Unable to render the tickets table">
                        <TicketsContainer />
                      </ErrorBoundary>
                    )}
                  />
                  <Route
                    exact
                    key={`${MenuItems.plans}/mission-details`}
                    path={`/${MenuItems.plans}/mission-details`}
                    render={() => (
                      <ErrorBoundary message="Unable to render the missions details">
                        <MissionDetailsContainer />
                      </ErrorBoundary>
                    )}
                  />
                  <Route
                    exact
                    key={`/${MenuItems.plans}/flight-information`}
                    path={`/${MenuItems.plans}/flight-information`}
                    render={() => (
                      <ErrorBoundary message="Unable to render the flight information">
                        <FlightInfoContainer />
                      </ErrorBoundary>
                    )}
                  />
                  <Route
                    exact
                    key={`/${MenuItems.plans}/performance-outlook`}
                    path={`/${MenuItems.plans}/performance-outlook`}
                    render={() => (
                      <ErrorBoundary message="Unable to render Performance Outlook">
                        <PerformanceOutlookContainer />
                      </ErrorBoundary>
                    )}
                  />
                  <Route
                    exact
                    key={`/${MenuItems.plans}/export`}
                    path={`/${MenuItems.plans}/export`}
                    render={() => (
                      <ErrorBoundary message="Unable to render Performance Outlook export page">
                        <PerformanceOutlookExportContainer />
                      </ErrorBoundary>
                    )}
                  />
                  <Route
                    key={MenuItems.plans}
                    path={`/${MenuItems.plans}`}
                    render={() => (
                      <ErrorBoundary message="Unable to render the Mission Plans page">
                        <PlansContainer />
                      </ErrorBoundary>
                    )}
                  />
                  <Route
                    exact
                    key={`/${MenuItems.dataUsage}`}
                    path={`/${MenuItems.dataUsage}`}
                    render={() => (
                      <ErrorBoundary message="Unable to render Data usage reports page">
                        <DataUsageContainer />
                      </ErrorBoundary>
                    )}
                  />
                  <Route
                    key={`/${MenuItems.provisioning}`}
                    path={`/${MenuItems.provisioning}`}
                    render={() => (
                      <ErrorBoundary message="Unable to render Provisioning page">
                        <ProvisioningContainer />
                      </ErrorBoundary>
                    )}
                  />
                  <Route
                    exact
                    key={`/${MenuItems.manualDowntime}`}
                    path={`/${MenuItems.manualDowntime}`}
                    render={() => (
                      <ErrorBoundary message="Unable to render Manual Downtime page">
                        <ManualDowntimeContainer />
                      </ErrorBoundary>
                    )}
                  />
                  <Route
                    exact
                    key={`/${MenuItems.transportMonitoring}`}
                    path={`/${MenuItems.transportMonitoring}`}
                    render={() => (
                      <ErrorBoundary message="Unable to render Transport Monitoring page">
                        <TransportMonitoringContainer />
                      </ErrorBoundary>
                    )}
                  />
                  <Route
                    exact
                    key={`/${MenuItems.transportMonitoring}/vehicleId`}
                    path={`/${MenuItems.transportMonitoring}/:vehicleId`}
                    render={() => (
                      <ErrorBoundary message="Unable to render Transport Monitoring page">
                        <FullSpaceVehicleViewContainer />
                      </ErrorBoundary>
                    )}
                  />
                  <Route render={() => <FleetMap />} />
                </Switch>
              </React.Suspense>
            </BodyWrapper>
            {isAuthenticated && (
              <MapContainer>
                <MapContext.Consumer>
                  {map => (
                    <div>
                      <ErrorBoundary message="Unable to render the hubs">
                        <HubsContainer map={map} />
                      </ErrorBoundary>
                      <ErrorBoundary message="Unable to render the weather overlay">
                        <WeatherOverlay map={map} />
                      </ErrorBoundary>
                    </div>
                  )}
                </MapContext.Consumer>
                <>
                  <PerformanceOutlookLegendPanel />
                  <OverlaysPanel />
                  <TailTagOverlaysPanel />
                  <MapSettingsPanel />
                  <RulerToolDistanceSummary />
                </>
                <Switch>
                  <Redirect exact key="root" from="/" to={MenuItems.fleet} />
                  <Route
                    exact
                    key={MenuItems.fleet}
                    path={`/${MenuItems.fleet}`}
                    render={() => (
                      <ErrorBoundary message="Unable to render the terminals on the map">
                        <AircraftFleetMapContainer />
                      </ErrorBoundary>
                    )}
                  />
                  <Route
                    exact
                    key={`/${MenuItems.fleet}/datapathUuid`}
                    path={`/${MenuItems.fleet}/:datapathUuid`}
                    render={(props: any) => (
                      <MapContext.Consumer>
                        {map => (
                          <ErrorBoundary message="Unable to render the terminals on the map">
                            <FullTailViewMapContainer map={map} />
                          </ErrorBoundary>
                        )}
                      </MapContext.Consumer>
                    )}
                  />
                  <Route
                    exact
                    key={`/${MenuItems.fleet}/datapathUuid/export`}
                    path={`/${MenuItems.fleet}/:datapathUuid/export`}
                    render={(props: any) => (
                      <MapContext.Consumer>
                        {map => (
                          <ErrorBoundary message="Unable to render the terminals on the map">
                            <FullTailViewMapContainer map={map} />
                          </ErrorBoundary>
                        )}
                      </MapContext.Consumer>
                    )}
                  />
                  <Route
                    exact
                    key={`/${MenuItems.plans}/performance-outlook`}
                    path={`/${MenuItems.plans}/performance-outlook`}
                    render={(props: any) => (
                      <MapContext.Consumer>
                        {map => (
                          <ErrorBoundary message="Unable to render Performance Outlook features on the map">
                            <PerformanceOutlookMap map={map} />
                          </ErrorBoundary>
                        )}
                      </MapContext.Consumer>
                    )}
                  />
                  <Route
                    exact
                    key={`/${MenuItems.transportMonitoring}/vehicleId`}
                    path={`/${MenuItems.transportMonitoring}/:vehicleId`}
                    render={(props: any) => (
                      <MapContext.Consumer>
                        {map => (
                          <ErrorBoundary message="Unable to render the space vehicles on the map">
                            <FullSpaceVehicleViewMapContainer map={map} />
                          </ErrorBoundary>
                        )}
                      </MapContext.Consumer>
                    )}
                  />
                </Switch>
              </MapContainer>
            )}
          </ScrollbarsPrintModeWrapper>
        </Body>
      </ThemeProvider>
    </AppWrapper>
  );
}

export default App;
